import Axios from 'axios'

import {
  TKeyMetricApi,
  TKeyMetricDataBody,
  TKeyMetricRawData,
  UpdateKeyMetricOrderItem
} from 'types/GlobalKeyMetric'
import { TGlobalFilterApiInput } from 'types/GlobalGlobalFilter'
import { TResourceVariant } from 'types/GlobalResource'
import { RollingType } from 'types/GlobalKpiOption'
import { ShowPerEnum, TMetricSettings } from 'types/GlobalMetric'

import { parseAuthenticationHeader } from 'helpers/Functions'
import { getWithRedirect } from 'utils/axios'

export type TPostKeyMetricBody = {
  kpi_template_id: string
  parent_id?: string | null
  title: string
  responsible_account_id?: string | null
} & TMetricSettings

export type TPatchKeyMetricBody = {
  kpi_template_id?: string
  rolling_sum?: RollingType | null
  distribution?: ShowPerEnum | null
  slices?: string[]
  variant_kpi_template_id?: string | null
  group_ids?: string[]
  resources?: { id: string; type: TResourceVariant }[]
  responsible_account_id?: string | null
  target_activated?: boolean
  target_value?: number | null
  title?: string
  increase_is_positive?: boolean | null
}

const BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990/'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

const URL = `${BASE_URL}v1/key-metrics`

export async function getAll() {
  return getWithRedirect(URL)
}

export async function create(body: TPostKeyMetricBody) {
  return Axios.post<{ data: TKeyMetricApi }>(
    URL,
    { data: body },
    parseAuthenticationHeader()
  )
}

export async function update(id: string, body: TPatchKeyMetricBody) {
  return Axios.patch<{ data: TKeyMetricApi }>(
    `${URL}/${id}`,
    {
      data: body
    },
    parseAuthenticationHeader()
  )
}

export async function updateOrder(body: UpdateKeyMetricOrderItem[]) {
  return Axios.put<{ data: TKeyMetricApi[] }>(
    `${URL}/update-order`,
    { data: body },
    parseAuthenticationHeader()
  )
}

export async function destroy(id: string) {
  return Axios.delete(`${URL}/${id}`, parseAuthenticationHeader())
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

export async function getData(
  id: string,
  globalFilter: TGlobalFilterApiInput,
  body?: TKeyMetricDataBody
) {
  const requestBody = {
    data: {
      ...body,
      query_params: {
        ...(body?.query_params || {}),
        global_filter: globalFilter
      }
    }
  }

  for (let attempt = 0; attempt <= 3; attempt++) {
    try {
      return await Axios.post<{ data: TKeyMetricRawData }>(
        `${URL}/${id}/data`,
        requestBody,
        parseAuthenticationHeader()
      )
    } catch (e) {
      if ((e as any)?.response?.status === 429 && attempt < 3) {
        await sleep(3000)
        continue
      }
      throw e
    }
  }

  // unreachable, but satisfies TypeScript
  throw new Error('getData: exhausted retries')
}
